// GrievanceModuleHome.jsx
// Standalone Grievance Management module — a faithful React port of the
// "Grievance Management" section from the combined.html mockup.
//
// Self-contained UI with hardcoded sample data (no backend). Styled with inline
// styles + design tokens so it renders cleanly without external CSS, matching the
// Issue Tracking / Expense module approach. Charts use recharts.

import React, { useMemo, useState } from "react";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  Legend,
  PieChart,
  Pie,
  Cell,
} from "recharts";
import CardLessDataTable from "../datatables/CardLessDataTableWithSearch";

// ─── Design tokens (from the mockup :root) ─────────────────────────────────────
const T = {
  primary: "#6366f1",
  primaryDark: "#4f46e5",
  success: "#10b981",
  warning: "#f59e0b",
  danger: "#ef4444",
  info: "#0ea5e9",
  purple: "#8b5cf6",
  text: "#1e293b",
  muted: "#64748b",
  border: "#e2e8f0",
  cardShadow: "0 4px 24px rgba(0,0,0,0.05)",
};

// ─── Sample data ───────────────────────────────────────────────────────────────
const STAT_CARDS = [
  { icon: "fal fa-inbox", color: "#6366f1", val: "248", lbl: "Total Grievances", sub: "This month • +6.4%" },
  { icon: "fal fa-check-double", color: "#10b981", val: "196", lbl: "Resolved", sub: "79% resolution rate" },
  { icon: "fal fa-clock", color: "#f59e0b", val: "38", lbl: "Open / In Progress", sub: "38 items awaiting action" },
  { icon: "fal fa-fire", color: "#ef4444", val: "14", lbl: "Escalated (SLA Breached)", sub: "4 new this week" },
];

const TREND_DATA = [
  { d: "24", raised: 14, resolved: 10 }, { d: "25", raised: 18, resolved: 14 },
  { d: "26", raised: 10, resolved: 9 }, { d: "27", raised: 22, resolved: 18 },
  { d: "28", raised: 16, resolved: 14 }, { d: "29", raised: 20, resolved: 17 },
  { d: "30", raised: 17, resolved: 15 }, { d: "1", raised: 19, resolved: 16 },
  { d: "2", raised: 15, resolved: 13 }, { d: "3", raised: 21, resolved: 19 },
  { d: "4", raised: 18, resolved: 15 }, { d: "5", raised: 14, resolved: 12 },
  { d: "6", raised: 20, resolved: 17 }, { d: "7", raised: 17, resolved: 15 },
];

const CATEGORY_DATA = [
  { name: "Hostel", value: 28, color: "#6366f1" },
  { name: "Academic", value: 22, color: "#10b981" },
  { name: "Exam", value: 16, color: "#f59e0b" },
  { name: "Payroll", value: 14, color: "#ef4444" },
  { name: "Transport", value: 12, color: "#0ea5e9" },
  { name: "Other", value: 8, color: "#8b5cf6" },
];

const GRIEVANCES = [
  { id: "GRV-1042", avatar: "RK", by: "Ravi Kumar (Student)", category: "Hostel Facility", priority: "High", assignee: "Warden Office", status: "Escalated" },
  { id: "GRV-1043", avatar: "AN", by: "Anitha N (Faculty)", category: "Payroll Discrepancy", priority: "Medium", assignee: "Accounts Dept", status: "In Progress" },
  { id: "GRV-1044", avatar: "SD", by: "Sandeep D (Student)", category: "Exam Malpractice", priority: "High", assignee: "Academic Board", status: "Open" },
  { id: "GRV-1045", avatar: "PJ", by: "Priya J (Student)", category: "Transport Delay", priority: "Low", assignee: "Transport In-charge", status: "Resolved" },
];

const CATEGORIES = [
  { name: "Hostel Facility", sla: "72 hrs", active: true },
  { name: "Academic", sla: "96 hrs", active: true },
  { name: "Exam Malpractice", sla: "48 hrs", active: true },
  { name: "Harassment / Ragging", sla: "24 hrs", active: true },
];

const COMMITTEE = [
  { avatar: "DW", name: "Dr. Warden", tier: "Tier 1", danger: false },
  { avatar: "HD", name: "HOD Academics", tier: "Tier 1", danger: false },
  { avatar: "PR", name: "Principal", tier: "Tier 2", danger: true },
];

const TIMELINE = [
  { title: "Grievance Raised", meta: "05 Jul 2026, 10:20 AM — Submitted via student portal" },
  { title: "Acknowledged by Warden Office", meta: "05 Jul 2026, 02:10 PM — Assigned for review" },
  { title: "Under Investigation", meta: "06 Jul 2026, 09:00 AM — Site inspection scheduled" },
  { title: "SLA Breached — Auto Escalated", meta: "07 Jul 2026, 10:20 AM — Escalated to Tier 2 (Principal Office)" },
];

const ESCALATIONS = [
  { avatar: "RK", title: "GRV-1042 — Hostel Facility (Ravi Kumar)", meta: "SLA breached by 4 hrs • Escalated 07 Jul 2026", tier: "Tier 2: Principal", danger: true },
  { avatar: "SD", title: "GRV-1044 — Exam Malpractice (Sandeep D)", meta: "SLA breached by 12 hrs • Escalated 06 Jul 2026", tier: "Tier 1: Academic Board", danger: false },
];

const STATUS_BADGE = {
  Resolved: { bg: "#ecfdf5", color: T.success },
  Active: { bg: "#ecfdf5", color: T.success },
  "In Progress": { bg: "#e0f2fe", color: T.info },
  Open: { bg: "#fffbeb", color: T.warning },
  Escalated: { bg: "#fef2f2", color: T.danger },
};

const PRIORITY = {
  High: { bg: "#fef2f2", color: T.danger },
  Medium: { bg: "#fffbeb", color: T.warning },
  Low: { bg: "#f0fdf4", color: T.success },
};

const TABS = [
  { key: "dashboard", label: "Dashboard", icon: "fa-chart-pie" },
  { key: "raise", label: "Raise Grievance", icon: "fa-plus-circle" },
  { key: "categories", label: "Categories & Committee", icon: "fa-layer-group" },
  { key: "tracker", label: "Resolution Tracker", icon: "fa-list-check" },
  { key: "escalations", label: "Escalations", icon: "fa-exclamation-triangle", count: 4 },
];

// ─── Small reusable bits ───────────────────────────────────────────────────────
function Badge({ children, kind }) {
  const s = STATUS_BADGE[kind] || { bg: "#f1f5f9", color: T.muted };
  return <span style={{ padding: "4px 12px", borderRadius: 20, fontSize: 11.5, fontWeight: 600, background: s.bg, color: s.color }}>{children}</span>;
}

function Priority({ level }) {
  const s = PRIORITY[level] || { bg: "#f1f5f9", color: T.muted };
  return <span style={{ padding: "3px 10px", borderRadius: 6, fontSize: 11, fontWeight: 700, background: s.bg, color: s.color }}>{level}</span>;
}

function TierBadge({ children, danger }) {
  return <span style={{ fontSize: 10.5, fontWeight: 700, padding: "3px 10px", borderRadius: 20, background: danger ? "#fef2f2" : "#eef2ff", color: danger ? T.danger : T.primary }}>{children}</span>;
}

function Avatar({ text, size = 28, bg = T.primary }) {
  return <span style={{ width: size, height: size, borderRadius: "50%", background: bg, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: size < 32 ? 11 : 13, fontWeight: 700, flexShrink: 0 }}>{text}</span>;
}

function Panel({ children, style }) {
  return <div style={{ background: "#fff", borderRadius: 16, padding: 20, boxShadow: T.cardShadow, border: `1px solid ${T.border}`, ...style }}>{children}</div>;
}

const thStyle = { textAlign: "left", fontSize: 11.5, textTransform: "uppercase", letterSpacing: ".4px", color: T.muted, padding: "12px 20px", background: "#f8fafc", fontWeight: 700 };
const tdStyle = { padding: "14px 20px", fontSize: 13.5, borderTop: `1px solid ${T.border}`, color: T.text };


function Field({ label, children, style }) {
  return <div style={{ ...style }}><label style={{ fontSize: 12.5, fontWeight: 600, color: T.text, marginBottom: 6, display: "block" }}>{label}</label>{children}</div>;
}

const inputStyle = { width: "100%", padding: "11px 14px", border: `1px solid ${T.border}`, borderRadius: 10, fontSize: 13.5, outline: "none", background: "#fff", color: T.text };

// ─── Main component ────────────────────────────────────────────────────────────
export default function GrievanceModuleHome() {
  const [tab, setTab] = useState("dashboard");
  const [anon, setAnon] = useState(false);

  return (
    <div style={{ background: "linear-gradient(135deg,#f0f4ff 0%,#faf5ff 100%)", minHeight: "100vh", padding: "24px 28px", color: T.text }}>
      {/* Topbar */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 22, flexWrap: "wrap", gap: 12 }}>
        <div>
          <h1 style={{ fontSize: 22, fontWeight: 800, margin: 0 }}>Grievance Management</h1>
          <p style={{ color: T.muted, fontSize: 13, marginTop: 2, marginBottom: 0 }}>
            Register, track, and resolve student/staff grievances with SLA-based escalation
          </p>
        </div>
        <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
          <button style={btn("outline")}><i className="fas fa-file-export" /> Export</button>
        </div>
      </div>

      {/* Tabs + content wrapped in a single card — same "folder tab" design as
          the Finance/Expense module (nav-tabs on a #f8fafc sub-header). */}
      <div style={{ background: "#fff", borderRadius: 16, boxShadow: T.cardShadow, overflow: "hidden", border: `1px solid ${T.border}` }}>
        <div style={{ background: "#f8fafc", borderBottom: `1.5px solid ${T.border}`, padding: "12px 24px 0 24px" }}>
          <ul className="nav nav-tabs" style={{ borderBottom: "none", gap: 8, marginBottom: 0, display: "flex", flexWrap: "wrap" }}>
            {TABS.map((t) => {
              const active = tab === t.key;
              return (
                <li className="nav-item" key={t.key}>
                  <a
                    className={`nav-link ${active ? "active" : ""}`}
                    href="#"
                    onClick={(e) => { e.preventDefault(); setTab(t.key); }}
                    style={{
                      cursor: "pointer", fontWeight: 700, fontSize: 14, display: "flex", alignItems: "center", gap: 8,
                      padding: "12px 20px",
                      color: active ? T.primary : "#4b5563",
                      background: active ? "#fff" : "transparent",
                      borderColor: active ? `${T.border} ${T.border} #fff` : "transparent",
                      borderWidth: "1.5px",
                      borderBottomWidth: active ? "2px" : "1.5px",
                      borderRadius: "10px 10px 0 0",
                      transition: "all 0.15s ease",
                      marginBottom: "-1.5px",
                    }}
                  >
                    <i className={`fas ${t.icon}`} /> {t.label}
                    {t.count ? <span style={{ background: T.danger, color: "#fff", borderRadius: 10, fontSize: 10, padding: "1px 6px" }}>{t.count}</span> : null}
                  </a>
                </li>
              );
            })}
          </ul>
        </div>

        <div style={{ padding: 28 }}>
          {tab === "dashboard" && <Dashboard />}
          {tab === "raise" && <RaiseGrievance anon={anon} setAnon={setAnon} />}
          {tab === "categories" && <Categories />}
          {tab === "tracker" && <Tracker />}
          {tab === "escalations" && <Escalations />}
        </div>
      </div>
    </div>
  );
}

// ─── Dashboard view ────────────────────────────────────────────────────────────
function Dashboard() {
  return (
    <div>
      <div style={grid(4)}>
        {STAT_CARDS.map((c, i) => (
          <div key={i} style={{
            background: "#fff", border: "1px solid #e5e7eb", borderTop: `4px solid ${c.color}`,
            borderRadius: 16, padding: "18px 20px", boxShadow: "0 4px 12px rgba(0,0,0,0.03)",
            display: "flex", alignItems: "center", gap: 16,
          }}>
            <div style={{ background: `${c.color}15`, color: c.color, borderRadius: 12, width: 50, height: 50, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
              <i className={c.icon} style={{ fontSize: 22 }} />
            </div>
            <div>
              <div style={{ fontSize: 12, color: "#6b7280", fontWeight: 600 }}>{c.lbl}</div>
              <div style={{ fontSize: 21, fontWeight: 800, color: "#1f2937", lineHeight: 1.2 }}>{c.val}</div>
              <div style={{ fontSize: 11, color: "#9ca3af", marginTop: 2 }}>{c.sub}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "1.6fr 1fr", gap: 18, margin: "22px 0" }}>
        <Panel>
          <h3 style={panelH3}>Grievance Trend</h3>
          <div style={panelSub}>Raised vs Resolved — last 14 days</div>
          <ResponsiveContainer width="100%" height={260}>
            <BarChart data={TREND_DATA} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
              <CartesianGrid stroke="#f1f5f9" vertical={false} />
              <XAxis dataKey="d" tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
              <YAxis tick={{ fontSize: 11, fill: T.muted }} axisLine={false} tickLine={false} />
              <Tooltip cursor={{ fill: "rgba(99,102,241,.06)" }} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="raised" name="Raised" fill={T.primary} radius={[6, 6, 0, 0]} maxBarSize={16} />
              <Bar dataKey="resolved" name="Resolved" fill={T.success} radius={[6, 6, 0, 0]} maxBarSize={16} />
            </BarChart>
          </ResponsiveContainer>
        </Panel>
        <Panel>
          <h3 style={panelH3}>Category Breakdown</h3>
          <div style={panelSub}>Share of grievances by category</div>
          <ResponsiveContainer width="100%" height={260}>
            <PieChart>
              <Pie data={CATEGORY_DATA} dataKey="value" nameKey="name" cx="50%" cy="45%" innerRadius={58} outerRadius={88} paddingAngle={2}>
                {CATEGORY_DATA.map((e, i) => <Cell key={i} fill={e.color} />)}
              </Pie>
              <Tooltip formatter={(v) => v + "%"} />
              <Legend iconType="circle" wrapperStyle={{ fontSize: 11 }} />
            </PieChart>
          </ResponsiveContainer>
        </Panel>
      </div>

      <Panel style={{ padding: 20 }}>
        <h3 style={{ fontSize: 15, fontWeight: 700, margin: "0 0 14px 0" }}>Recent Grievances</h3>
        <CardLessDataTable
          parentData={GRIEVANCES}
          particlecolumns={GRIEVANCE_COLUMNS}
          name="recent_grievances"
          searchBar={true}
          placeholder="Search ticket, name..."
        />
      </Panel>
    </div>
  );
}

// Columns for the Recent Grievances table (react-bootstrap-table via CardLessDataTable).
const GRIEVANCE_COLUMNS = [
  { dataField: "__sno", text: "#", isDummyField: true, sort: false, align: "center", headerAlign: "center", headerStyle: { width: "60px" }, formatter: (cell, row, i) => i + 1 },
  {
    dataField: "id", text: "Ticket #", sort: true,
    formatter: (cell) => <span style={{ background: "#f1f5f9", color: "#475569", borderRadius: 6, padding: "2px 8px", fontSize: 11, fontWeight: 600 }}>{cell}</span>,
  },
  {
    dataField: "by", text: "Raised By", sort: true,
    formatter: (cell, row) => <span style={{ display: "flex", alignItems: "center", gap: 8 }}><Avatar text={row.avatar} /> {cell}</span>,
  },
  { dataField: "category", text: "Category", sort: true },
  { dataField: "priority", text: "Priority", sort: true, formatter: (cell) => <Priority level={cell} /> },
  { dataField: "assignee", text: "Assigned To", sort: true },
  { dataField: "status", text: "Status", sort: true, formatter: (cell) => <Badge kind={cell}>{cell}</Badge> },
];

// ─── Raise Grievance view ──────────────────────────────────────────────────────
function RaiseGrievance({ anon, setAnon }) {
  return (
    <div style={{ display: "grid", gridTemplateColumns: "1.4fr 1fr", gap: 20 }}>
      <Panel style={{ padding: 24 }}>
        <h3 style={{ marginBottom: 18, fontSize: 16, fontWeight: 700 }}>New Grievance Ticket</h3>

        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", background: "#f8fafc", border: `1px solid ${T.border}`, borderRadius: 10, padding: "12px 14px", marginBottom: 16 }}>
          <div>
            <div style={{ fontWeight: 600, fontSize: 13.5 }}>Submit Anonymously</div>
            <div style={{ fontSize: 11.5, color: T.muted }}>Identity hidden from committee, visible only to admin</div>
          </div>
          <div onClick={() => setAnon(!anon)} style={{ position: "relative", width: 42, height: 24, background: anon ? T.primary : "#cbd5e1", borderRadius: 20, cursor: "pointer", transition: ".2s" }}>
            <span style={{ position: "absolute", top: 2, left: anon ? 20 : 2, width: 20, height: 20, background: "#fff", borderRadius: "50%", transition: ".2s" }} />
          </div>
        </div>

        <div style={formRow}>
          <Field label="Raised By (Role)"><select style={inputStyle}><option>Student</option><option>Faculty</option><option>Parent</option><option>Staff</option></select></Field>
          <Field label="Branch / Department"><select style={inputStyle}><option>Main Campus</option><option>North Campus</option><option>Junior College</option></select></Field>
        </div>
        <div style={formRow}>
          <Field label="Grievance Category"><select style={inputStyle}><option>Hostel Facility</option><option>Academic</option><option>Exam Malpractice</option><option>Payroll</option><option>Transport</option><option>Harassment / Ragging</option></select></Field>
          <Field label="Priority"><select style={inputStyle} defaultValue="Medium"><option>Low</option><option>Medium</option><option>High</option></select></Field>
        </div>
        <Field label="Subject" style={{ marginBottom: 16 }}><input type="text" placeholder="Brief title of the grievance" style={inputStyle} /></Field>
        <Field label="Description" style={{ marginBottom: 16 }}><textarea rows="4" placeholder="Describe the issue in detail..." style={{ ...inputStyle, resize: "vertical" }} /></Field>
        <Field label="Attachments (Evidence)"><UploadBox text="Drag & drop photos/documents or click to upload" /></Field>
        <div style={{ background: "#eef2ff", border: "1px solid #c7d2fe", borderRadius: 10, padding: "12px 14px", fontSize: 12.5, color: T.primaryDark, marginTop: 10 }}>
          <i className="fas fa-info-circle" /> Based on category "Hostel Facility" and Medium priority, this ticket must be resolved within <strong>72 hours</strong>, or it auto-escalates to Tier 2.
        </div>
      </Panel>

      <Panel style={{ padding: 24, height: "fit-content" }}>
        <h3 style={{ marginBottom: 16, fontSize: 16, fontWeight: 700 }}>Assignment Preview</h3>
        <PreviewRow label="Auto-Assigned To" value="Warden Office" />
        <PreviewRow label="Tier 1 Reviewer" value="Dept. Committee" />
        <PreviewRow label="Escalation Path" value="Principal Office" />
        <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", fontSize: 16, fontWeight: 800, color: T.primaryDark }}>
          <span>SLA Deadline</span><span>72 hrs</span>
        </div>
        <button style={{ ...btn("primary"), width: "100%", justifyContent: "center", marginTop: 20 }}><i className="fas fa-paper-plane" /> Submit Grievance</button>
      </Panel>
    </div>
  );
}

function PreviewRow({ label, value }) {
  return <div style={{ display: "flex", justifyContent: "space-between", padding: "10px 0", fontSize: 13.5, borderBottom: `1px dashed ${T.border}` }}><span>{label}</span><span>{value}</span></div>;
}

// ─── Categories & Committee view ───────────────────────────────────────────────
const addFormRow = { display: "flex", gap: 10, flexWrap: "wrap", alignItems: "center", background: "#f8fafc", border: `1px solid ${T.border}`, borderRadius: 10, padding: 12, marginBottom: 14 };

function Categories() {
  const [categories, setCategories] = useState(CATEGORIES);
  const [committee, setCommittee] = useState(COMMITTEE);
  const [showCat, setShowCat] = useState(false);
  const [showCom, setShowCom] = useState(false);
  const [catName, setCatName] = useState("");
  const [comName, setComName] = useState("");

  const addCategory = () => {
    if (!catName.trim()) return;
    setCategories([...categories, { name: catName.trim(), active: true }]);
    setCatName(""); setShowCat(false);
  };
  const addMember = () => {
    if (!comName.trim()) return;
    const initials = comName.trim().split(/\s+/).map((w) => w[0]).join("").slice(0, 2).toUpperCase();
    setCommittee([...committee, { avatar: initials, name: comName.trim(), role: "Committee Member" }]);
    setComName(""); setShowCom(false);
  };

  return (
    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 18 }}>
      <Panel>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Grievance Categories</h3>
          <button style={{ ...btn("primary"), padding: "8px 14px", fontSize: 12.5 }} onClick={() => setShowCat((v) => !v)}>
            <i className={`fas ${showCat ? "fa-times" : "fa-plus"}`} /> {showCat ? "Close" : "New"}
          </button>
        </div>
        {showCat && (
          <div style={addFormRow}>
            <input style={{ ...inputStyle, flex: 1, minWidth: 150 }} placeholder="Category name" value={catName} onChange={(e) => setCatName(e.target.value)} />
            <button style={{ ...btn("primary"), padding: "9px 16px", fontSize: 13 }} onClick={addCategory}><i className="fas fa-check" /> Add</button>
          </div>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Category", "Status"].map((h) => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {categories.map((c, i) => (
              <tr key={`${c.name}-${i}`}>
                <td style={tdStyle}>{c.name}</td>
                <td style={tdStyle}><Badge kind="Active">Active</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
      <Panel>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14 }}>
          <h3 style={{ fontSize: 15, fontWeight: 700, margin: 0 }}>Redressal Committee</h3>
          <button style={{ ...btn("primary"), padding: "8px 14px", fontSize: 12.5 }} onClick={() => setShowCom((v) => !v)}>
            <i className={`fas ${showCom ? "fa-times" : "fa-plus"}`} /> {showCom ? "Close" : "New"}
          </button>
        </div>
        {showCom && (
          <div style={addFormRow}>
            <input style={{ ...inputStyle, flex: 1, minWidth: 150 }} placeholder="Member name" value={comName} onChange={(e) => setComName(e.target.value)} />
            <button style={{ ...btn("primary"), padding: "9px 16px", fontSize: 13 }} onClick={addMember}><i className="fas fa-check" /> Add</button>
          </div>
        )}
        <table style={{ width: "100%", borderCollapse: "collapse" }}>
          <thead><tr>{["Member", "Status"].map((h) => <th key={h} style={thStyle}>{h}</th>)}</tr></thead>
          <tbody>
            {committee.map((m, i) => (
              <tr key={`${m.name}-${i}`}>
                <td style={tdStyle}><span style={{ display: "flex", alignItems: "center", gap: 8 }}><Avatar text={m.avatar} /> {m.name}</span></td>
                <td style={tdStyle}><Badge kind="Active">Active</Badge></td>
              </tr>
            ))}
          </tbody>
        </table>
      </Panel>
    </div>
  );
}

// ─── Resolution Tracker view ───────────────────────────────────────────────────
function Tracker() {
  const [timeline, setTimeline] = useState(TIMELINE);
  const [note, setNote] = useState("");
  const [resolved, setResolved] = useState(false);

  const markResolved = () => {
    if (resolved) return;
    setTimeline([...timeline, {
      title: "Marked as Resolved",
      meta: note.trim() ? `Resolution note: ${note.trim()}` : "Resolved by Warden Office",
    }]);
    setNote("");
    setResolved(true);
  };

  return (
    <Panel>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: 8 }}>
        <div>
          <h3 style={{ fontSize: 15, fontWeight: 700 }}>Ticket GRV-1042 — Hostel Facility Issue</h3>
          <div style={panelSub}>Raised by Ravi Kumar • Assigned to Warden Office • SLA: 72 hrs</div>
        </div>
        <Badge kind={resolved ? "Resolved" : "Escalated"}>{resolved ? "Resolved" : "Escalated"}</Badge>
      </div>
      <div style={{ position: "relative", paddingLeft: 24, marginTop: 16 }}>
        <div style={{ position: "absolute", left: 6, top: 4, bottom: 4, width: 2, background: T.border }} />
        {timeline.map((t, i) => {
          const isResolvedItem = resolved && i === timeline.length - 1;
          const dot = isResolvedItem ? T.success : T.primary;
          return (
            <div key={i} style={{ position: "relative", marginBottom: 18 }}>
              <span style={{ position: "absolute", left: -24, top: 3, width: 12, height: 12, borderRadius: "50%", background: dot, border: "2px solid #fff", boxShadow: `0 0 0 2px ${dot}` }} />
              <div style={{ fontWeight: 700, fontSize: 13.5 }}>{t.title}</div>
              <div style={{ fontSize: 11.5, color: T.muted, marginTop: 2 }}>{t.meta}</div>
            </div>
          );
        })}
      </div>
      {!resolved && (
        <Field label="Add Resolution Note" style={{ marginTop: 20 }}>
          <textarea rows="3" placeholder="Update investigation status or resolution details..." style={{ ...inputStyle, resize: "vertical" }} value={note} onChange={(e) => setNote(e.target.value)} />
        </Field>
      )}
      <button
        style={{ ...btn("primary"), marginTop: 12, ...(resolved ? { background: T.success, boxShadow: "none", cursor: "default" } : {}) }}
        onClick={markResolved}
        disabled={resolved}
      >
        <i className="fas fa-check" /> {resolved ? "Resolved" : "Mark as Resolved"}
      </button>
    </Panel>
  );
}

// ─── Escalations view ──────────────────────────────────────────────────────────
const NEXT_TIER = { "Tier 1: Academic Board": "Tier 2: Principal", "Tier 2: Principal": "Tier 3: Management" };

function Escalations() {
  const [items, setItems] = useState(ESCALATIONS);

  const resolveNow = (idx) => setItems(items.filter((_, i) => i !== idx));
  const escalateFurther = (idx) =>
    setItems(items.map((e, i) => (i === idx ? { ...e, tier: NEXT_TIER[e.tier] || e.tier, danger: true } : e)));

  return (
    <div>
      {items.length === 0 && (
        <div style={{ background: "#fff", borderRadius: 16, padding: "40px 20px", boxShadow: T.cardShadow, border: `1px solid ${T.border}`, textAlign: "center", color: T.muted }}>
          <i className="fas fa-check-circle" style={{ fontSize: 30, color: T.success, marginBottom: 10, display: "block" }} />
          No pending escalations — all caught up.
        </div>
      )}
      {items.map((e, i) => (
        <div key={i} style={{ background: "#fff", borderRadius: 16, padding: "18px 20px", boxShadow: T.cardShadow, border: `1px solid ${T.border}`, display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 14, flexWrap: "wrap", gap: 12 }}>
          <div style={{ display: "flex", gap: 14, alignItems: "center" }}>
            <Avatar text={e.avatar} size={40} bg={e.danger ? T.danger : T.primary} />
            <div>
              <div style={{ fontWeight: 700, fontSize: 14 }}>{e.title}</div>
              <div style={{ fontSize: 12, color: T.muted, marginTop: 2 }}>{e.meta} <TierBadge danger={e.danger}>{e.tier}</TierBadge></div>
            </div>
          </div>
          <div style={{ display: "flex", gap: 10 }}>
            <button style={{ ...btn("plain"), background: T.success, color: "#fff" }} onClick={() => resolveNow(i)}><i className="fas fa-check" /> Resolve Now</button>
            <button style={{ ...btn("plain"), background: "#fff", color: T.danger, border: "1px solid #fecaca" }} onClick={() => escalateFurther(i)}><i className="fas fa-arrow-up" /> Escalate Further</button>
          </div>
        </div>
      ))}
    </div>
  );
}

// ─── Shared UI helpers ─────────────────────────────────────────────────────────
function UploadBox({ text }) {
  return (
    <div style={{ border: `2px dashed ${T.border}`, borderRadius: 14, padding: 30, textAlign: "center", color: T.muted, cursor: "pointer" }}>
      <i className="fas fa-cloud-upload-alt" style={{ fontSize: 26, color: T.primary, marginBottom: 8, display: "block" }} />
      <div>{text}</div>
    </div>
  );
}

// ─── Style helpers ─────────────────────────────────────────────────────────────
const panelH3 = { fontSize: 15, fontWeight: 700, marginBottom: 4 };
const panelSub = { fontSize: 12, color: T.muted, marginBottom: 14 };
const formRow = { display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 };

function grid(cols) {
  return { display: "grid", gridTemplateColumns: `repeat(${cols}, 1fr)`, gap: 18, marginBottom: 22 };
}

function btn(variant) {
  const base = { border: "none", borderRadius: 10, padding: "10px 18px", fontSize: 13.5, fontWeight: 600, cursor: "pointer", display: "flex", alignItems: "center", gap: 8, transition: ".15s" };
  if (variant === "primary") return { ...base, background: `linear-gradient(135deg,${T.primary},${T.primaryDark})`, color: "#fff", boxShadow: "0 4px 14px rgba(99,102,241,.35)" };
  if (variant === "outline") return { ...base, background: "#fff", color: T.text, border: `1px solid ${T.border}` };
  return base;
}
